import * as React from 'react'
import { Button, useEffectWithPolling, WidgetWrapper } from 'uxp/components'
import { IUIPropertyDefinitionMap, IWidgetTemplateInstanceProps, IWidgetTemplateProps, registerCardTemplate } from '../../../common/common'
import { generateIconProp } from '../../../common/icon-selector/IconEditor'
import { SelectableItem, selectUIProps } from '../../../common/SelectableItem'
import { ContainerProperty, extractContainerProperties } from '../../../common/ui-props/ContainerProperty'
import { extractTextProperties, TextProperty } from "../../../common/ui-props/TextProperty";
import { DataSourceProperty } from "../../../common/ui-props/DataSourceProperty";
import useDimensions from 'react-cool-dimensions';
import { IPopulationPercentageWidgetProps, PopulationPercentageWidgetProperty } from './PopulationPercentageWidgetProperty'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ThemeSelector } from '../../../common/theme-selector/theme-selector'
import classNames from 'classnames'
import { hasValue, replaceWithData } from '../../../../util'
import { IContextData, useInitialContextData } from '../../../../hooks/useContextData'
import './styles.scss'
import { executeAndBind, processInitialSource } from '../../../common/data-source-picker/datasource'

const DefaultChartProperties: IPopulationPercentageWidgetProps = {
    refreshInterval: 60000,
    channel: '',
    firstValueField: '',
    secondValueField: '',
    secondValueConstant: '',
    icon: 'userAlt',
    iconColor: '#424242',
    label: 'Items',
    labelColor: '#424242',
    secondValueIsTotal: false,

};

const XPR: React.ForwardRefExoticComponent<React.RefAttributes<IWidgetTemplateInstanceProps> & IWidgetTemplateProps> = React.forwardRef((props, ref) => {

    const { designer } = props;
    let uiProps = props?.uiProps || {};

    const [firstValue, setFirstValue] = React.useState(0);
    const [secondValue, setSecondValue] = React.useState(0);
    const [loading, setLoading] = React.useState(false)
    let [contextData, setContextData] = React.useState<IContextData>({})
    const [initialSource, setIntitialSource] = React.useState(props.initialSource);

    const { isLoading, initialContextData } = useInitialContextData(props, [uiProps?.source])

    const XPRProps: IUIPropertyDefinitionMap = {
        "headerContainer": ContainerProperty({
            background: {
                color: "",
                image: {
                    image: "",
                    position: "",
                    size: "",
                },
            }
        }),
        'title': TextProperty({ text: 'Item Distribution', color: '#424242', size: '14px' }, 'Title'),
        'source': DataSourceProperty('Source', uiProps.source, uiProps.datasources, props.uxpContext, props.designer, 'dictionary'),
        'chartProperty': {
            'all': PopulationPercentageWidgetProperty('all', uiProps.chartProperty || DefaultChartProperties),
            'icon': PopulationPercentageWidgetProperty('icon', uiProps.chartProperty || DefaultChartProperties),
            'label': PopulationPercentageWidgetProperty('label', uiProps.chartProperty || DefaultChartProperties),

        },
    }

    let { styles: headerStyles } = extractContainerProperties(uiProps.headerContainer)
    let { text: title, styles: titleStyles } = extractTextProperties(uiProps.title, 'Item Distribution')
    let chartProps: IPopulationPercentageWidgetProps = props.uiProps.chartProperty || DefaultChartProperties;

    let { refreshInterval, channel } = chartProps;
    let { width, height, observe } = useDimensions();

    let minIconSize = 30;
    let iconMargin = 20;
    let cols = Math.floor(width / (minIconSize + 2 * iconMargin));
    let rows = Math.floor(height / (minIconSize + 2 * iconMargin));
    let iconCount = rows * cols;


    let total = firstValue + secondValue;
    if (chartProps?.secondValueIsTotal) {
        total = secondValue;
    }
    if (total < 1) total = 1;
    if (total < iconCount) {
        iconCount = Math.max(total, 1);
    }
    if (iconCount < cols && iconCount > 0) {
        minIconSize = width / iconCount - 2 * iconMargin;
    }

    React.useImperativeHandle(ref, () => ({
        beforeSave: validateBeforeSave,
        onPreview: validateBeforeSave
    }))

    React.useEffect(() => {
        return () => {
            designer?.removeErrors?.(['datasource', 'first-value', 'second-value'])
        }
    }, [])

    React.useEffect(() => {
        validateBeforeSave()
    }, [uiProps])

    React.useEffect(() => {
        setLoading(isLoading)
        setContextData(prev => ({ ...prev, ...initialContextData }))
    }, [isLoading, initialContextData])

    React.useEffect(() => {

        processInitialSource(props.designer, props.uxpContext, props.uiProps, initialSource, 'dictionary')
            .then(res => {
                if (res) props.designer?.setUIProps(res)
            })
            .catch(e => {
                console.log('exception ', e);

            })

    }, [initialSource]);

    useEffectWithPolling(props.uxpContext, channel || "chart/1", refreshInterval || 60000, async () => {
        if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
            setFirstValue(40);
            setSecondValue(60);
            return;
        }

        executeAndBind(props.uxpContext, uiProps.source, uiProps.datasources, {}, contextData)
            .then(res => {
                let s = Number(chartProps.secondValueConstant);
                let f = Number(res[chartProps.firstValueField || 'count']);
                if (!s) {
                    s = Number(res[chartProps.secondValueField || 'count1']);
                }

                setFirstValue(f);
                setSecondValue(s);

            })
    }, [props.designer]);


    // this will be called before save
    function validateBeforeSave() {
        if (!isDataSourceValid()) return false
        if (!hasFieldsConfigured()) return false
        return true
    }

    function isDataSourceValid() {
        if (!hasValue(uiProps?.source) || !hasValue(uiProps.source?.source) || !hasValue(uiProps.source?.path)) {
            designer?.addErrors?.([{
                id: 'datasource',
                type: 'property',
                error: 'Data source is required',
                containerId: 'chart-container',
                fieldId: 'source'
            }])
            return false
        }
        designer?.removeErrors?.(['datasource'])
        return true
    }

    function hasFieldsConfigured() {
        let hasFirstValue = hasValue(uiProps?.chartProperty?.firstValueField);
        let hasSecondValue = (
            hasValue(uiProps?.chartProperty?.secondValueField)
            || hasValue(uiProps?.chartProperty?.secondValueConstant)
        );
        if (!hasFirstValue) {

            designer?.addErrors?.([{
                id: 'first-value',
                type: 'property',
                error: 'First value field is required',
                containerId: 'chart-container',
                fieldId: 'first-value'
            }])
            return false;
        }
        designer?.removeErrors?.(['first-value'])

        if (!hasSecondValue) {
            designer?.addErrors?.([{
                id: 'second-value',
                type: 'property',
                error: 'Second value field is required',
                containerId: 'chart-container',
                fieldId: 'second-value'
            }])
            return false;
        }
        designer?.removeErrors?.(['second-value'])
        return true
    }


    function loop<T>(count: number, cb: (c: number) => T) {
        let items: T[] = [];
        for (let i = 0; i < count; i++) {
            items.push(cb(i));
        }
        return items;
    }

    const chartLabel = replaceWithData(chartProps?.label, contextData)

    return <WidgetWrapper className='uxp-population-percentage-widget-template'>

        <SelectableItem
            style={headerStyles}
            className={'ww'}
            showAtRuntime={true}
            designer={props.designer}
            props={selectUIProps(uiProps, XPRProps, 'source', 'headerContainer')}
            contextData={contextData}
        >
            <div className='title' style={titleStyles}>
                <SelectableItem
                    designer={props.designer}
                    props={selectUIProps(uiProps, XPRProps, 'title')}
                    contextData={contextData}
                >
                    {title}

                </SelectableItem>
            </div>
            <div className='icons' >
                <SelectableItem
                    designer={props.designer}
                    props={selectUIProps(uiProps, XPRProps, 'source', 'chartProperty.all')}
                    elementId='chart-container'
                    contextData={contextData}
                >
                    <div className='icon-grid' ref={observe}>
                        <div className='icon-grid-container' >
                            {
                                loop(iconCount, (c) => <div className={classNames('icon-g', { 'faded': (c / iconCount) > (firstValue / total) })}
                                    style={{ margin: `${iconMargin}px` }}>
                                    <FontAwesomeIcon style={{ width: `${minIconSize}px`, height: `${minIconSize}px`, }} color={chartProps.iconColor} icon={generateIconProp(chartProps.icon)} />
                                </div>)
                            }
                        </div>
                    </div>
                </SelectableItem>
            </div>
            <div className='data-text' style={{ color: chartProps?.labelColor }} title={`${firstValue} / ${total}`}>
                <SelectableItem
                    designer={props.designer}
                    props={selectUIProps(uiProps, XPRProps, 'source', 'chartProperty.label')}
                    contextData={contextData}
                >
                    <div className='data'>{firstValue}</div>
                    <div className='subtxt'>{chartLabel}</div>
                </SelectableItem>
            </div>

        </SelectableItem>

    </WidgetWrapper>;
})
interface IWidgetTheme {
    background: string;
    text: string;
}
let Themes: IWidgetTheme[] = [
    {
        background: '#8CA5BF',
        text: 'white',
    },
    {
        background: "#EBEBEB",
        text: "#424242",
    },
    {
        background: "#E8F1FE",
        text: "#424242",
    },
    {
        background: "#FAE4A9",
        text: "#424242",
    },
    {
        background: "#262626",
        text: "#FFF",
    },
    {
        background: "#EFECE9",
        text: "#424242",
    },
    {
        background: "#E4EDD9",
        text: "#424242",
    },
    {
        background: "#1B457B",
        text: "#FFF",
    },
    {
        background: "#F7D0BF",
        text: "#424242",
    },
];
const TB: React.FunctionComponent<IWidgetTemplateProps> = props => {
    let [showingThemeSelector, setShowingThemeSelector] = React.useState(false);
    return <><div className='template-toolbar-content'>
        <Button
            icon={'fas eye'}
            title={'Select a theme'}
            onClick={() => {
                setShowingThemeSelector(!showingThemeSelector);
            }}
            className='nav-btn'
        />


    </div>
        {showingThemeSelector && <ThemeSelector
            onClose={() => {
                setShowingThemeSelector(false);
            }}
            onRender={(props) => {
                return <div style={{ position: 'absolute', left: '0px', right: '0px', top: '0px', bottom: '0px', justifyContent: 'center', alignItems: 'center', padding: '20px', background: props.background, display: 'flex', flexWrap: 'wrap' }}>
                    <FontAwesomeIcon style={{ margin: '10px', width: '30px', height: '30px', color: props.text }} icon={generateIconProp('userAlt')} />
                    <FontAwesomeIcon style={{ margin: '10px', width: '30px', height: '30px', color: props.text }} icon={generateIconProp('userAlt')} />
                    <FontAwesomeIcon style={{ margin: '10px', width: '30px', height: '30px', color: props.text }} icon={generateIconProp('userAlt')} />
                    <FontAwesomeIcon style={{ opacity: 0.5, margin: '10px', width: '30px', height: '30px', color: props.text }} icon={generateIconProp('userAlt')} />
                </div>;
            }}
            themes={Themes}
            onSelect={(_p) => {

                let newProps = Object.assign({}, props.uiProps);
                if (!newProps.chartProperty) newProps.chartProperty = DefaultChartProperties;
                newProps.chartProperty.labelColor = _p.text;
                newProps.chartProperty.iconColor = _p.text;
                if (!newProps.headerContainer) newProps.headerContainer = {};
                if (!newProps.headerContainer.background) newProps.headerContainer.background = { color: '' };
                newProps.headerContainer.background.color = _p.background;

                if (!newProps.title) newProps.title = { text: '', color: '', size: '14px' };
                newProps.title.color = _p.text;
                props.designer.setUIProps(newProps);
            }}
        />}
    </>;

};

registerCardTemplate({
    id: 'population-percentage-widget',
    name: 'Population Percentage Widget',
    description: 'This widget visualizes how many items out of a total are valid/invalid with a visual depiction of quantities',
    template: XPR,
    toolbar: TB,
    icon: generateIconProp('braille'),
    complexity: 'easy',
    preLoader: 'donut-chart',
    expectedSchema: 'dictionary'
});
